/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the patient summary component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * React
 */
import * as React from 'react';


import { makeStyles, Theme } from '@material-ui/core/styles';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';

/**
 * Used to format text.
 */

/**
 * Used to style expanding areas.
 */

/**
 * Used to style expanding areas.
 */

/**
 * Used to style expanding areas.
 */


import { RequestState } from '@ngt/request-utilities';



import CircularProgress from '@material-ui/core/CircularProgress';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { faChevronUp } from '@fortawesome/pro-duotone-svg-icons/faChevronUp';
import Collapse from '@material-ui/core/Collapse';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { IEvent, IEventType, IForm, IFormType, IPatient, useForms } from '@ngt/online-patient-management-ui';
import PatientSummaryForm from './PatientSummaryForm';


/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

interface IPatientSummaryFormsProps {
    patient: IPatient;
    eventType: IEventType;
    event: IEvent | null;
    eventRepeat: number;
    formType: IFormType;
    loadingEvents: boolean;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        paddingLeft: theme.spacing(6)
    },
    item: {
        marginTop: -1,
        border: `1px solid ${theme.palette.grey[300]}`
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */


const PatientSummaryForms: React.FunctionComponent<IPatientSummaryFormsProps> = ({
    patient,
    formType,
    event,
    eventRepeat,
    eventType,
    loadingEvents
}) => {
    if (!patient) {
        throw new Error('Patient must be defined.');
    }

    if (!eventType) {
        throw new Error('eventType must be defined.');
    }

    if (!formType) {
        throw new Error('formType must be defined.');
    }

    if (!eventRepeat) {
        throw new Error('eventRepeat must be defined.');
    }

    const classes = useStyles();

    const [forms, formsLoadState, formsActions] = useForms(formType.id as number, patient.id as number, patient.institutionId as number, event?.id ?? null, eventType.id as number, eventRepeat);

    React.useEffect(() => {
        if (event?.id) {
            formsActions.load();

            return () => {
                formsActions.clear();
            }
        }

        return () => {
            return;
        }
    }, [formsActions, event?.id]);

    const [expand, setExpand] = React.useState(true);

    const onClick = React.useCallback(() => {
        setExpand(!expand);
    }, [setExpand, expand]);

    const eventTypeFormType = React.useMemo(() => {
        return eventType?.formTypes?.find(ft => ft.formTypeId === formType.id) ?? null;
    }, [eventType, formType])

    const loadingForms = loadingEvents || (!!event?.id && (formsLoadState.state === RequestState.None || formsLoadState.state === RequestState.Pending));

    const showFormType = React.useMemo(() => {
        return eventTypeFormType?.states.some(etfts => etfts.visible && etfts.patientStatus === patient.status) ?? false;
    }, [eventTypeFormType, patient]);

    const components = React.useMemo(() => {
        if (loadingForms || !showFormType) {
            return null;
        }

        if (forms && forms.length > 0) {
            if (!eventTypeFormType?.repeating || !eventTypeFormType?.minimumRepeat) {
                return (
                    <>
                        {
                            forms.map((form: IForm) => {
                                return (
                                    <PatientSummaryForm
                                        key={form.formRepeat ?? 1}
                                        eventType={eventType}
                                        eventRepeat={eventRepeat}
                                        formRepeat={form.formRepeat ?? 1}
                                        form={form}
                                        event={event}
                                        eventTypeFormType={eventTypeFormType}
                                        formType={formType}
                                        loadingForms={loadingForms}
                                        patient={patient}
                                    />
                                );
                            })
                        }
                    </>
                );
            }

            const minimumArray = Array.from(Array(eventTypeFormType?.minimumRepeat), (e, i) => i + 1);

            return (
                <>
                    {
                        minimumArray.map(i => {
                            const form = forms.find((f: IForm) => f.formRepeat === i) ?? null;

                            return (
                                <PatientSummaryForm
                                    key={i}
                                    eventType={eventType}
                                    eventRepeat={eventRepeat}
                                    formRepeat={i}
                                    form={form}
                                    event={event}
                                    eventTypeFormType={eventTypeFormType}
                                    formType={formType}
                                    loadingForms={loadingForms}
                                    patient={patient}
                                />
                            );
                        })
                    }
                    {
                        forms.filter((form: IForm) => (form.formRepeat ?? 1) > (eventTypeFormType?.minimumRepeat ?? 1)).map(form => {
                            return (
                                <PatientSummaryForm
                                    key={form.formRepeat ?? 1}
                                    eventType={eventType}
                                    eventRepeat={eventRepeat}
                                    formRepeat={form.formRepeat ?? 1}
                                    form={form}
                                    event={event}
                                    eventTypeFormType={eventTypeFormType}
                                    formType={formType}
                                    loadingForms={loadingForms}
                                    patient={patient}
                                />
                            );
                        })
                    }
                </>
            );
        }

        if (!eventTypeFormType?.repeating) {
            return (
                <PatientSummaryForm
                    key={1}
                    eventType={eventType}
                    eventRepeat={eventRepeat}
                    formRepeat={1}
                    form={null}
                    event={event}
                    eventTypeFormType={eventTypeFormType}
                    formType={formType}
                    loadingForms={loadingForms}
                    patient={patient}
                />
            );
        }

        if (eventTypeFormType?.minimumRepeat) {
            const minimumArray = Array.from(Array(eventTypeFormType.minimumRepeat), (e, i) => i + 1);

            return (
                <>
                    {
                        minimumArray.map(i => {
                            return (
                                <PatientSummaryForm
                                    key={i}
                                    eventType={eventType}
                                    eventRepeat={eventRepeat}
                                    formRepeat={i}
                                    form={null}
                                    event={event}
                                    eventTypeFormType={eventTypeFormType}
                                    formType={formType}
                                    loadingForms={loadingForms}
                                    patient={patient}
                                />
                            );
                        })
                    }
                </>
            );
        }

        return null;

    }, [forms, event, eventType, eventTypeFormType, formType, patient, loadingForms, eventRepeat, showFormType]);

    if (!showFormType) {
        return null;
    }

    if (loadingForms) {
        return (
            <ListItem
                button
                className={classes.item}
                onClick={onClick}
            >
                <ListItemIcon>
                    <CircularProgress size={16} />
                </ListItemIcon>
                <ListItemText primary={<strong>{formType.name}</strong>} />
            </ListItem>
        )
    }

    if (eventTypeFormType?.repeating) {
        return (
            <>
                <ListItem
                    button
                    className={classes.item}
                    onClick={onClick}
                >
                    <ListItemIcon>
                        <FontAwesomeIcon fixedWidth icon={expand ? faChevronUp : faChevronDown} />
                    </ListItemIcon>
                    <ListItemText primary={<strong>{formType.name}</strong>} />
                </ListItem>
                <Collapse
                    in={expand}
                >
                    <div
                        className={classes.container}
                    >
                        {components}
                    </div>
                </Collapse>
            </>
        );
    }

    return (
        components
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default PatientSummaryForms;
